<template>
  <div class="calm">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">Transactions · v2</div>
          <h1 class="calm__header__title">{{ $t("menu.trans") }}</h1>
          <div class="calm__header__sub">
            <span><strong class="c-mono">{{ totalCount }}</strong> รายการ</span>
            <template v-if="selectedRows.length">
              <span class="calm__header__sep">·</span>
              <span><strong class="c-mono" style="color:var(--c-blue-deep)">{{ selectedRows.length }}</strong> เลือกแล้ว</span>
            </template>
            <span class="calm__header__sep">·</span>
            <span class="c-mono">{{ todayStamp }}</span>
          </div>
        </div>
        <div class="calm__header__actions">
          <button class="c-btn c-btn--primary" @click="handleCreate()">
            <i class="fas fa-plus"></i> {{ $t("new") }}
          </button>
          <router-link :to="{ name: 'TransCancel' }" class="c-btn c-btn--outline">
            <i class="fas fa-ban"></i> {{ $t("menu.trans_cancel") }}
          </router-link>
        </div>
      </div>
    </header>

    <div class="calm__body">
      <!-- ═══════════════ FILTER CARD ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">ตัวกรอง</div>
            <div class="calm__card__hint">เลือกเงื่อนไขเพื่อกรองรายการในบัญชี</div>
          </div>
        </div>

        <div class="calm__filter-grid trans-filter-grid">
          <div class="fld">
            <label>{{ $t("lmg.start_date") }}</label>
            <flat-picker
              :config="{ allowInput: true }"
              class="ed c-mono"
              v-model="searchParams.startDate"
            ></flat-picker>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.end_date") }}</label>
            <flat-picker
              :config="{ allowInput: true }"
              class="ed c-mono"
              v-model="searchParams.endDate"
            ></flat-picker>
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.policy_number") }}</label>
            <input
              type="text"
              class="ed c-mono"
              v-model="searchParams.policyNumber"
              :placeholder="$t('lmg.policy_number')"
            />
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.license_plate") }}</label>
            <input
              type="text"
              class="ed c-mono"
              v-model="searchParams.licencePlate"
              :placeholder="$t('lmg.license_plate')"
              @keyup.enter="handleSearch()"
            />
            <span v-if="plateTermTooShort" class="fld__warn">
              {{ $t("lmg.license_plate_min") }}
            </span>
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.insure") }}</label>
            <el-select v-model="searchParams.insuranceID" :placeholder="$t('lmg.insure')" filterable>
              <el-option
                v-for="option in tableInsureWithAll"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.owner_person") }}</label>
            <el-select v-model="searchParams.ownerPersonID" :placeholder="$t('lmg.owner_person')" filterable>
              <el-option
                v-for="option in tablePersonDropdownWithAll"
                :key="option.ID"
                :label="(option.FName + ' ' + option.LName).trim()"
                :value="option.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("status") }}</label>
            <el-select v-model="searchParams.status" :placeholder="$t('status')">
              <el-option
                v-for="option in status"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>สถานะการชำระ</label>
            <el-select v-model="searchParams.PayFilter" placeholder="สถานะการชำระ">
              <el-option
                v-for="option in payFilterOptions"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.cash_clear") }}</label>
            <el-select v-model="searchParams.cashClear" :placeholder="$t('lmg.cash_clear')">
              <el-option
                v-for="option in cashClear"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.sort") }}</label>
            <el-select v-model="searchParams.sort" :placeholder="$t('lmg.sort')">
              <el-option
                v-for="option in sort"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              ></el-option>
            </el-select>
          </div>
        </div>

        <div class="trans-filter-actions">
          <button class="c-btn c-btn--ghost" :disabled="isLoading" @click="handleResetFilters()">
            <i class="fas fa-undo"></i> {{ $t("clear") }}
          </button>
          <button class="c-btn c-btn--soft" v-if="tableData.length > 0" @click="handleExportCSV()">
            <i class="fas fa-file-csv"></i> CSV
          </button>
          <button class="c-btn c-btn--soft" v-if="tableData.length > 0" @click="handleExportXLSX()">
            <i class="fas fa-file-excel"></i> XLSX
          </button>
          <button class="c-btn c-btn--primary" :disabled="isLoading" @click="handleSearch()">
            <i class="fas fa-spinner fa-spin" v-if="isLoading"></i>
            <i class="fas fa-search" v-else></i>
            {{ $t("search") }}
          </button>
        </div>
      </div>

      <!-- ═══════════════ TABLE CARD ═══════════════ -->
      <div class="calm__card" style="padding:0;margin-top:16px">
        <div class="trans-table-head">
          <div>
            <div class="calm__card__title">รายการ</div>
            <div class="calm__card__hint" v-if="totalCount">
              {{ from + 1 }}–{{ to }} จาก {{ totalCount }} รายการ
              <template v-if="pageTotalAmount">
                <span class="calm__header__sep">·</span>
                รวมเบี้ยในหน้านี้ <strong class="c-mono">{{ pageTotalLabel }}</strong>
              </template>
            </div>
          </div>
          <div class="trans-table-toolbar">
            <input
              v-model="searchQuery"
              type="text"
              class="ed"
              :placeholder="$t('search') + '…'"
              style="width:240px;max-width:100%"
            />
            <div class="calm__tfoot__per">
              <span>แสดงต่อหน้า</span>
              <el-select v-model="pagination.perPage" style="width:90px">
                <el-option v-for="n in perPageOptions" :key="n" :label="n" :value="n"></el-option>
              </el-select>
            </div>
          </div>
        </div>

        <el-table
          :data="queriedData"
          row-key="id"
          @sort-change="sortChange"
          @selection-change="selectionChange"
          :empty-text="isLoading ? 'กำลังโหลด…' : 'ไม่พบรายการ'"
        >
          <el-table-column label="ประเภท" min-width="130">
            <template slot-scope="{ row }">
              <span class="class-chip">{{ row.TransactionTypeName }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.owner_person')" min-width="170">
            <template slot-scope="{ row }">
              <div class="stack">
                <span class="stack__primary">{{ row.OwnerPersonFName }} {{ row.OwnerPersonLName }}</span>
                <span class="stack__caption">ตัวแทน</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.customer')" min-width="180">
            <template slot-scope="{ row }">
              <div class="stack">
                <span class="stack__primary">{{ row.CustomerFname }} {{ row.CustomerLname }}</span>
                <span class="stack__caption">ผู้เอาประกัน</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.policy_number')" min-width="250">
            <template slot-scope="{ row }">
              <div
                v-if="inlineEdit.id !== row.ID || inlineEdit.field !== 'policy'"
                class="stack"
              >
                <div class="inline-cell">
                  <span class="c-mono">{{ row.PolicyNumber || "—" }}</span>
                  <button class="icon-btn" :title="$t('lmg.edit')" @click="beginInlineEditPolicy(row)">
                    <i class="fas fa-pen"></i>
                  </button>
                </div>
                <span
                  v-if="row.SettleStatus === 1"
                  class="calm__pill calm__pill--success settle-pill"
                >{{ row.InvoiceNumber ? ("ใบวางบิล " + row.InvoiceNumber + " · ชำระแล้ว") : "ชำระแล้ว" }}</span>
                <span
                  v-else-if="row.SettleStatus === 2"
                  class="calm__pill calm__pill--warning settle-pill"
                >สำรองจ่าย</span>
                <span
                  v-else-if="row.InvoiceNumber"
                  class="stack__caption"
                >ใบวางบิล {{ row.InvoiceNumber }}</span>
              </div>
              <div v-else class="inline-cell inline-cell--edit">
                <input
                  class="inline-input c-mono"
                  v-model="inlineEdit.value"
                  :disabled="inlineEdit.saving"
                  @keyup.enter="saveInlineEditPolicy(row)"
                  @keyup.esc="cancelInlineEdit()"
                />
                <button class="icon-btn icon-btn--confirm" :disabled="inlineEdit.saving" @click="saveInlineEditPolicy(row)">
                  <i class="fas fa-spinner fa-spin" v-if="inlineEdit.saving"></i>
                  <i class="fas fa-check" v-else></i>
                </button>
                <button class="icon-btn" :disabled="inlineEdit.saving" @click="cancelInlineEdit()">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.license_plate')" min-width="150">
            <template slot-scope="{ row }">
              <span class="c-mono">{{ row.LicencePlate || "—" }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.dms_number')" min-width="120" align="center">
            <template slot-scope="{ row }">
              <div v-if="row.DMSNumber" class="stack">
                <a
                  class="c-mono dms-link"
                  :href="'#/lmg/dms?batch=' + encodeURIComponent(row.DMSNumber.split(',')[0].trim())"
                  target="_blank"
                  rel="noopener"
                  :title="'เปิดใบนำส่ง DMS ' + row.DMSNumber"
                >{{ row.DMSNumber }}</a>
                <span v-if="row.DMSDate" class="stack__caption">{{ row.DMSDate }}</span>
              </div>
              <span v-else class="c-soft">—</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.protection_date')" min-width="280">
            <template slot-scope="{ row }">
              <div
                v-if="inlineEdit.id !== row.ID || inlineEdit.field !== 'dates'"
                class="inline-cell"
              >
                <span class="c-mono date-range">
                  <span>{{ row.StartDateShow }}</span>
                  <span class="dash">→</span>
                  <span>{{ row.EndDateShow }}</span>
                </span>
                <button class="icon-btn" :title="$t('lmg.edit')" @click="beginInlineEditDates(row)">
                  <i class="fas fa-pen"></i>
                </button>
              </div>
              <div v-else class="inline-cell inline-cell--edit">
                <flat-picker
                  :config="{ allowInput: true }"
                  class="inline-input c-mono"
                  style="max-width: 110px"
                  v-model="inlineEdit.startDate"
                  :disabled="inlineEdit.saving"
                ></flat-picker>
                <span class="dash">→</span>
                <flat-picker
                  :config="{ allowInput: true }"
                  class="inline-input c-mono"
                  style="max-width: 110px"
                  v-model="inlineEdit.endDate"
                  :disabled="inlineEdit.saving"
                ></flat-picker>
                <button class="icon-btn icon-btn--confirm" :disabled="inlineEdit.saving" @click="saveInlineEditDates(row)">
                  <i class="fas fa-spinner fa-spin" v-if="inlineEdit.saving"></i>
                  <i class="fas fa-check" v-else></i>
                </button>
                <button class="icon-btn" :disabled="inlineEdit.saving" @click="cancelInlineEdit()">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.amount')" min-width="130" align="right">
            <template slot-scope="{ row }">
              <span class="c-mono money" :class="{ 'money--flag': row.MoreDiscount > 0 }">
                {{ FormatCurrency ? FormatCurrency(row.Amount) : row.Amount }}
              </span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.discount')" min-width="120" align="right">
            <template slot-scope="{ row }">
              <span class="c-mono money-soft">
                {{ FormatCurrency ? FormatCurrency(row.CommissionAmount) : row.CommissionAmount }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="คอมฯ คืนตัวแทน" min-width="150" align="center">
            <template slot-scope="{ row }">
              <!-- Only sales the agent paid IN FULL (PaidFull=1) leave the branch
                   owing the commission back. Click the pill to toggle paid/owed. -->
              <button
                v-if="row.PaidFull === 1"
                type="button"
                class="calm__pill commission-toggle"
                :class="row.CommissionPaidToAgent === 1 ? 'calm__pill--success' : 'calm__pill--warning'"
                :disabled="commissionSaving[row.ID]"
                :title="'คลิกเพื่อสลับสถานะ · ค่าคอมฯ ' + (FormatCurrency ? FormatCurrency(row.CommissionAmount) : row.CommissionAmount)"
                @click="toggleCommissionPaid(row)"
              >
                <i class="fas fa-spinner fa-spin" v-if="commissionSaving[row.ID]"></i>
                <template v-else>{{ row.CommissionPaidToAgent === 1 ? 'จ่ายคืนแล้ว' : 'ค้างจ่ายคืน' }}</template>
              </button>
              <span v-else class="c-mute" title="ตัวแทนหักคอมฯ ตอนจ่าย — ไม่ติดค้าง">—</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('status')" min-width="150">
            <template slot-scope="{ row }">
              <span class="calm__pill" :class="statusPillClass(row.Status)">
                <template v-if="row.Status == 1">สำเร็จ</template>
                <template v-else-if="row.Status == 2">กำลังดำเนินการ</template>
                <template v-else-if="row.Status == 3">ยกเลิก</template>
              </span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('lmg.sale_date')" min-width="130">
            <template slot-scope="{ row }">
              <span class="c-mono">{{ row.SaleDateShow }}</span>
            </template>
          </el-table-column>

          <el-table-column label="จัดการ" align="right" min-width="180">
            <template slot-scope="{ $index, row }">
              <div class="row-actions">
                <button class="rowbtn" @click="handleReport(row)" title="รายงาน">
                  <i class="fa fa-file-alt"></i>
                </button>
                <button class="rowbtn" @click="handleDetail(row)" title="รายละเอียด">
                  <i class="fa fa-expand"></i>
                </button>
                <button class="rowbtn" @click="handlePaymentTable($index, row)" title="ชำระเงิน">
                  <i class="fas fa-dollar-sign"></i>
                </button>
                <button
                  class="rowbtn"
                  v-if="row.HasBilling != 0"
                  @click="handleBilling($index, row)"
                  title="เรียกเก็บ"
                >
                  <i class="fas fa-file-invoice-dollar"></i>
                </button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="calm__tfoot" style="padding:14px 22px" v-if="totalCount > 0">
          <div class="calm__tfoot__count">
            แสดง <strong>{{ from + 1 }}</strong>–<strong>{{ to }}</strong> จาก <strong>{{ totalCount }}</strong> รายการ
          </div>
          <base-pagination
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="totalCount"
          ></base-pagination>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <transaction-detail-modal
      :show.sync="showDetailModal"
      :transaction="detail"
      @saved="onDetailSaved"
      @cancel-trans="onCancelTrans"
    ></transaction-detail-modal>

    <!-- ═══════════════ REPORT MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showReportModal"
      :title="$t('lmg.trans_report')"
      size="md"
      :show-footer="false"
    >
      <div class="trans-report" v-if="detail">
        <div class="fld">
          <label>{{ $t("lmg.discount") }} (%)</label>
          <input
            type="number"
            class="ed c-mono"
            min="0"
            v-model.number="trans_report.discount"
            :placeholder="$t('lmg.discount')"
          />
        </div>
      </div>
      <template slot="footer" slot-scope="{ close }">
        <button type="button" class="c-btn c-btn--ghost" @click="close">
          {{ $t("close") }}
        </button>
        <button class="c-btn c-btn--soft" @click="handleDiscountReport()">
          <i class="fas fa-file-pdf"></i> {{ $t("download") }}
        </button>
        <button
          class="c-btn c-btn--primary"
          v-if="detail.TaxAmount > 0"
          @click="handleTaxReport()"
        >
          <i class="fas fa-file-pdf"></i> {{ $t("download") }} ภาษีส่วนลด
        </button>
      </template>
    </calm-modal>

    <!-- ═══════════════ CANCEL MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showCancelModal"
      :title="$t('lmg.cancel_trans')"
      size="md"
      :show-footer="false"
    >
      <div class="trans-cancel" v-if="detail">
        <div class="trans-cancel__id c-mono">
          {{ $t("lmg.transaction_id") }} : <strong>{{ detail.ID }}</strong>
        </div>
        <div class="fld">
          <label>{{ $t("lmg.effect_date") }}</label>
          <flat-picker
            :config="{ allowInput: true }"
            class="ed c-mono"
            v-model="cancelParams.effect_date"
          ></flat-picker>
        </div>
        <div class="fld">
          <label>{{ $t("lmg.note") }}</label>
          <textarea
            class="ed"
            rows="3"
            v-model="cancelParams.note"
            :placeholder="$t('lmg.note')"
          ></textarea>
        </div>
      </div>
      <template slot="footer" slot-scope="{ close }">
        <button type="button" class="c-btn c-btn--ghost" @click="close">
          {{ $t("close") }}
        </button>
        <button
          class="c-btn c-btn--primary"
          v-if="cancelParams.effect_date != ''"
          @click="CancelTransaction()"
        >
          <i class="fas fa-ban"></i> {{ $t("save") }}
        </button>
      </template>
    </calm-modal>

    <!-- ═══════════════ PAYMENT-TABLE MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showPaymentTableModal"
      :title="$t('menu.payment')"
      size="lg"
      :show-footer="false"
    >
      <div class="pay-modal">
        <el-table
          :data="tablePayment"
          row-key="id"
          :empty-text="'ไม่พบรายการ'"
        >
          <el-table-column :label="$t('lmg.recorder')" min-width="160">
            <template slot-scope="{ row }">
              {{ row.RecordPersonFName }} {{ row.RecordPersonLName }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('lmg.amount')" min-width="120" align="right">
            <template slot-scope="{ row }">
              <span class="c-mono money">
                {{ FormatCurrency ? FormatCurrency(row.Amount) : row.Amount }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('lmg.payment_method')" min-width="130">
            <template slot-scope="{ row }">
              <span class="class-chip">{{ paymentMethodName(row.PaidVia) }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('lmg.trans_date')" min-width="150">
            <template slot-scope="{ row }">
              <span class="c-mono">{{ row.CreatedAt }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template slot="footer" slot-scope="{ close }">
        <button type="button" class="c-btn c-btn--ghost" @click="close">
          {{ $t("close") }}
        </button>
      </template>
    </calm-modal>

    <!-- ═══════════════ BILLING MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showBillingModal"
      :title="$t('menu.billing')"
      size="lg"
      :show-footer="false"
    >
      <div class="pay-modal">
        <el-table
          :data="tableBilling"
          row-key="id"
          :empty-text="'ไม่พบรายการ'"
        >
          <el-table-column :label="$t('lmg.recorder')" min-width="160">
            <template slot-scope="{ row }">
              {{ row.OwnerFName }} {{ row.OwnerLName }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('lmg.tel')" min-width="120">
            <template slot-scope="{ row }">
              <span class="c-mono">{{ row.OwnerTel }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('lmg.amount')" min-width="120" align="right">
            <template slot-scope="{ row }">
              <span class="c-mono money">
                {{ FormatCurrency ? FormatCurrency(row.PayAmount) : row.PayAmount }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('lmg.due_date')" min-width="130">
            <template slot-scope="{ row }">
              <span class="c-mono">{{ row.DueDate }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('status')" min-width="160">
            <template slot-scope="{ $index, row }">
              <span class="calm__pill" :class="billingPillClass(row.Paid)">
                {{ billingStatusName(row.Paid) }}
              </span>
              <button
                v-if="row.Paid == 2"
                class="rowbtn"
                style="margin-left:6px"
                title="ชำระเงิน"
                @click="handleBillingPayment($index, row)"
              >
                <i class="fas fa-dollar-sign"></i>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template slot="footer" slot-scope="{ close }">
        <button type="button" class="c-btn c-btn--ghost" @click="close">
          {{ $t("close") }}
        </button>
      </template>
    </calm-modal>

    <!-- ═══════════════ PAYMENT-FORM MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showPaymentFormModal"
      :title="$t('lmg.payment_create')"
      size="md"
      :show-footer="false"
    >
      <div class="pay-form">
        <div class="fld">
          <label>{{ $t("lmg.trans_payment_amount") }}</label>
          <input
            type="text"
            class="ed c-mono"
            :value="payment_form.amount"
            readonly
            v-if="payment_form.billID != 0"
          />
          <input
            type="text"
            class="ed c-mono"
            v-model="payment_form.amount"
            v-if="payment_form.billID == 0"
          />
        </div>

        <div class="fld">
          <label>{{ $t("lmg.moreDiscount") }}</label>
          <input
            type="text"
            class="ed c-mono"
            :value="payment_form.moreDiscount"
            readonly
          />
        </div>

        <div class="fld">
          <label>{{ $t("lmg.billing_paid_via") }}</label>
          <el-select
            v-model="payment_form.paidVia"
            :placeholder="$t('lmg.billing_paid_via')"
          >
            <el-option
              v-for="option in paymentType"
              :key="option.ID"
              :label="option.Name"
              :value="option.ID"
            ></el-option>
          </el-select>
        </div>

        <div class="fld" v-if="payment_form.paidVia == 3">
          <label>{{ $t("lmg.bank") }}</label>
          <el-select v-model="payment_form.bankID" :placeholder="$t('lmg.bank')">
            <el-option
              v-for="option in tableBank"
              :key="option.ID"
              :label="option.Name"
              :value="option.ID"
            ></el-option>
          </el-select>
        </div>

        <div class="fld">
          <label>{{ $t("lmg.billing_note") }}</label>
          <textarea
            class="ed"
            rows="2"
            v-model="payment_form.note"
            :placeholder="$t('lmg.billing_note')"
          ></textarea>
        </div>

        <div class="fld">
          <label>{{ $t("lmg.paid_file") }}</label>
          <file-input @change="filesPaymentFormChange"></file-input>
        </div>
      </div>
      <template slot="footer" slot-scope="{ close }">
        <button type="button" class="c-btn c-btn--ghost" @click="close">
          {{ $t("close") }}
        </button>
        <button class="c-btn c-btn--primary" @click="handleCreatePayment()">
          <i class="fas fa-save"></i> {{ $t("save") }}
        </button>
      </template>
    </calm-modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import { BasePagination } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import * as XLSX from "xlsx";
import swal from "sweetalert2";
import FileInput from "@/components/Inputs/FileInput";
import TransactionDetailModal from "./modals/TransactionDetailModal.vue";

export default {
  name: "TransactionV2List",
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    flatPicker,
    FileInput,
    TransactionDetailModal,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },

  data() {
    return {
      propsToSearch: ["PolicyNumber", "LicencePlate", "CustomerFname", "CustomerLname"],
      selectedRows: [],
      perPageOptions: [10, 25, 50, 100],
      commissionSaving: {}, // { [transactionID]: bool } — per-row toggle in-flight
      searchParams: {
        startDate: "",
        endDate: "",
        sort: 1,
        status: 0,
        insuranceID: 0,
        cashClear: 0,
        policyNumber: "",
        licencePlate: "",
        ownerPersonID: 0,
        PayFilter: 0,
      },
      status: [
        { ID: 0, Name: "ทั้งหมด" },
        { ID: 1, Name: "สำเร็จ" },
        { ID: 2, Name: "กำลังดำเนินการ" },
      ],
      payFilterOptions: [
        { ID: 0, Name: "ทั้งหมด" },
        { ID: 1, Name: "สร้างใบวางบิล" },
        { ID: 2, Name: "ชำระแล้ว" },
        { ID: 3, Name: "ยังไม่ชำระ" },
        { ID: 4, Name: "สำรองจ่าย" },
        { ID: 5, Name: "ชำระเงินสด" },
        { ID: 6, Name: "โอนตรงเข้าสนญ." },
        { ID: 7, Name: "โอนเข้าสาขา" },
        { ID: 8, Name: "เงินสดที่สาขา" },
        { ID: 9, Name: "มีหมายเลข DMS" },
      ],
      sort: [
        { ID: 0, Name: "เรียงตามวันที่ล่าสุด" },
        { ID: 1, Name: "เรียงตามวันที่เริ่มต้น" },
      ],
      cashClear: [
        { ID: 0, Name: "ทั้งหมด" },
        { ID: 1, Name: "ไม่มีเงินสดค้างที่พนักงาน" },
        { ID: 2, Name: "มีเงินสดคงค้างที่พนักงาน" },
        { ID: 3, Name: "ไม่ใช้เงินสด ไม่มีเงินค้าง" },
      ],
      showDetailModal: false,
      showReportModal: false,
      showCancelModal: false,
      showPaymentTableModal: false,
      showBillingModal: false,
      showPaymentFormModal: false,
      trans_report: { discount: 0 },
      cancelParams: { effect_date: "", note: "" },
      // payment_form mirrors v1 verbatim (same keys/defaults)
      payment_form: {
        tID: 0,
        amount: 0,
        paidVia: 1, // payment type
        billID: 0,
        bankID: 0,
        note: "",
        tfimg: null,
        moreDiscount: 0,
      },
      // paid-via options (mirror v1 data)
      paymentType: [
        { ID: 1, Name: "เงินสด" },
        { ID: 2, Name: "เช็ค" },
        { ID: 3, Name: "โอนเงิน" },
        { ID: 4, Name: "เงินสำรองจ่าย" },
        { ID: 5, Name: "บัตรเครดิต" },
      ],
      detail: null,
      inlineEdit: {
        id: null,
        field: null,
        value: "",
        startDate: "",
        endDate: "",
        saving: false,
      },
    };
  },

  computed: {
    ...mapGetters({
      tableDataOrg: "TransactionV2/getData",
      tableInsureWithAll: "Insure/getDataWithAll",
      tablePersonDropdownWithAll: "Person/getDataDropdownWithAll",
      isLoading: "TransactionV2/isLoading",
      tableBank: "Bank/getData",
      tablePayment: "Payment/getData",
      tableBilling: "Billing/getData",
    }),
    tableData() {
      return this.tableDataOrg;
    },
    totalCount() {
      return (this.tableData || []).length;
    },
    todayStamp() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${yyyy}/${mm}/${dd} ${hh}:${mi}`;
    },
    pageTotalAmount() {
      return (this.queriedData || []).reduce((s, r) => s + (Number(r.Amount) || 0), 0);
    },
    pageTotalLabel() {
      return new Intl.NumberFormat("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(this.pageTotalAmount);
    },
    // The server ignores a 1-character plate term. Say so, rather than letting
    // the user stare at a list that silently lacks the condition they typed.
    // Drives the hint only — it never blocks the search (see handleSearch) —
    // and renders whether the value was typed or arrived via ?licencePlate=.
    plateTermTooShort() {
      return (this.searchParams.licencePlate || "").trim().length === 1;
    },
  },

  async mounted() {
    this.searchParams.startDate = this.getStartDate();
    this.searchParams.endDate = this.getEndDate();
    // Allow callers (invoice detail modal / transaction view) to deep-link a
    // policy number: /lmg/v2/transaction?policyNumber=XYZ pre-fills the search
    // before listing (mirrors the retired v1 behavior). Same for a licence
    // plate: /lmg/v2/transaction?licencePlate=กข1234.
    if (this.$route.query.policyNumber) {
      this.searchParams.policyNumber = this.$route.query.policyNumber;
    }
    if (this.$route.query.licencePlate) {
      this.searchParams.licencePlate = this.$route.query.licencePlate;
    }
    await this.listInsure();
    this.listPersonDropdown();
    this.listBank();
    this.listData(this.searchParams);
  },

  methods: {
    ...mapActions({
      updateCommissionPaid: "TransactionV2/updateCommissionPaid",
      listData: "TransactionV2/listData",
      listInsure: "Insure/listData",
      listPersonDropdown: "Person/listDropdown",
      updatePolicyNumberAction: "TransactionV2/updatePolicyNumber",
      updateDateAction: "TransactionV2/updateDate",
      cancelData: "TransactionV2/cancelData",
      getReportDiscount: "TransactionV2/ReportDiscount",
      getReporTaxDiscount: "TransactionV2/ReportTaxDiscount",
      listBank: "Bank/listData",
      listPayment: "Payment/listData",
      createPayment: "Payment/createData",
      listBilling: "Billing/listData",
      createBillingPayment: "Billing/createData",
    }),

    handleCreate() {
      this.$router.push({ name: "TransV2New" });
    },

    // Search button / Enter in the plate box.
    //
    // A sub-minimum plate term never blocks the search. The server drops just
    // that one condition and honours everything else, so a valid policy-number
    // search sitting next to a stray character in the plate box must still run;
    // refusing the whole form would strand the user on a hint that talks only
    // about the plate. plateTermTooShort drives the hint, nothing more.
    //
    // Page reset: queriedData slices tableData by currentPage, so searching
    // while parked on page 12 slices past the end of a smaller result set and
    // renders an empty table that reads as "not found".
    handleSearch() {
      this.pagination.currentPage = 1;
      this.listData(this.searchParams);
    },

    // Flip transaction.commission_paid_to_agent (Phase 1, issue #47). Optimistic
    // UI: update the row on success; revert on failure.
    toggleCommissionPaid(row) {
      if (this.commissionSaving[row.ID]) return;
      const next = row.CommissionPaidToAgent === 1 ? 0 : 1;
      this.$set(this.commissionSaving, row.ID, true);
      this.updateCommissionPaid({ ID: row.ID, paid: next })
        .then(() => {
          row.CommissionPaidToAgent = next;
        })
        .catch(() => {
          swal.fire({ title: "Error", text: "บันทึกสถานะคอมฯ ไม่สำเร็จ", icon: "error" });
        })
        .finally(() => {
          this.$set(this.commissionSaving, row.ID, false);
        });
    },

    handleResetFilters() {
      this.searchParams.startDate = this.getStartDate();
      this.searchParams.endDate = this.getEndDate();
      this.searchParams.sort = 1;
      this.searchParams.status = 0;
      this.searchParams.insuranceID = 0;
      this.searchParams.ownerPersonID = 0;
      this.searchParams.cashClear = 0;
      this.searchParams.policyNumber = "";
      this.searchParams.licencePlate = "";
      this.searchParams.PayFilter = 0;
      this.pagination.currentPage = 1;
      this.listData(this.searchParams);
    },

    selectionChange(selectedRows) {
      this.selectedRows = selectedRows;
    },

    pressmarkClass(s) {
      // kept for backward compat; new template uses statusPillClass
      if (s == 1) return "pressmark-chip--success";
      if (s == 2) return "pressmark-chip--pending";
      if (s == 3) return "pressmark-chip--danger";
      return "pressmark-chip--muted";
    },
    statusPillClass(s) {
      if (s == 1) return "calm__pill--success";
      if (s == 2) return "calm__pill--blue";
      if (s == 3) return "calm__pill--danger";
      return "calm__pill--neutral";
    },

    handleDetail(row) {
      this.detail = row;
      this.showDetailModal = true;
    },
    onDetailSaved() {
      this.listData(this.searchParams);
    },
    onCancelTrans(row) {
      // Detail modal asked to cancel this transaction. Mirror v1: set detail
      // then open the cancel modal (which seeds effect_date/note).
      this.detail = row;
      this.handleCancelTransaction();
    },

    // --- Report flow (mirrors v1) ----------------------------------------
    handleReport(row) {
      this.detail = row;
      this.showReportModal = true;
    },
    handleDiscountReport() {
      this.getReportDiscount({
        tID: this.detail.ID,
        discountPercent: this.trans_report.discount,
      })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "file.pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch(() => {});
    },
    handleTaxReport() {
      this.getReporTaxDiscount({ TID: this.detail.ID })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "file.pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch(() => {});
    },

    // --- Cancel flow (mirrors v1) ----------------------------------------
    handleCancelTransaction() {
      this.showCancelModal = true;
      this.cancelParams.effect_date = this.getEndDate();
      this.cancelParams.note = "";
    },
    CancelTransaction() {
      this.cancelData({
        transaction_id: this.detail.ID,
        effect_date: this.cancelParams.effect_date,
        note: this.cancelParams.note,
      })
        // Two-arg then: a hiccup in the success path (list refresh) must not
        // be reported to the operator as "ยกเลิกไม่สำเร็จ".
        .then(
          () => {
            this.showCancelModal = false;
            this.showDetailModal = false;
            this.listData(this.searchParams);
            swal.fire({
              title: this.$t("cancel_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          },
          (err) => {
            swal.fire({
              title: this.$t("cancel_fail"),
              text: this.cancelErrorText(err),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          }
        );
    },
    // One clear reason for a refused/failed cancel — never a bare "ไม่สำเร็จ".
    // Msg is an i18n key; a Thai Cause names the exact blocker (invoice /
    // DMS batch number, bad date, unknown sale). English Causes are developer
    // detail and are not shown. No response at all (network drop, proxy,
    // CORS) gets its own message instead of an empty dialog.
    cancelErrorText(err) {
      const res = err && err.response;
      if (!res) return this.$t("errors.network_unreachable");
      if (res.status === 401) return this.$t("errors.session_expired");
      const data = res.data || {};
      const msg =
        data.Msg && this.$te(data.Msg)
          ? this.$t(data.Msg)
          : this.$t("errors.unexpected_server_error", { code: res.status });
      const cause = typeof data.Cause === "string" ? data.Cause.trim() : "";
      if (cause && !/^[A-Za-z]/.test(cause) && cause !== msg) {
        return msg + "\n" + cause;
      }
      return msg;
    },

    // --- Payment / Billing flow (mirrors v1 verbatim) --------------------
    clearPayment() {
      this.payment_form.tID = 0;
      this.payment_form.amount = "";
      this.payment_form.paidVia = 1;
      this.payment_form.billID = 0;
      this.payment_form.bankID = 0;
      this.payment_form.note = "";
      this.payment_form.moreDiscount = "";
      this.payment_form.tfimg = null;
    },
    handlePaymentTable(index, row) {
      this.payment_form.tID = row.ID;
      this.listPayment({ tID: row.ID });
      this.showPaymentTableModal = true;
    },
    handleBilling(index, row) {
      this.detail = row;
      this.clearPayment();
      this.listBilling({ tID: row.ID });
      this.showBillingModal = true;
      this.payment_form.tID = row.ID;
    },
    handleBillingPayment(index, row) {
      this.detail = row;
      this.showPaymentFormModal = true;
      this.payment_form.billID = row.ID;
      // this.payment_form.amount = row.Amount;
      this.payment_form.amount = row.PayAmount;
      this.payment_form.bankID = this.tableBank[0].ID;
    },
    handlePaymentForm(row) {
      this.payment_form.tID = row.ID;
      // this.payment_form.amount = row.Amount;
      this.payment_form.amount = row.PayAmount;
      this.payment_form.bankID = this.tableBank[0].ID;
      this.showPaymentFormModal = true;
    },
    filesPaymentFormChange(files) {
      this.payment_form.tfimg = files[0];
    },
    handleCreatePayment() {
      this.showPaymentFormModal = false;

      let formData = new FormData();
      formData.append("tID", this.payment_form.tID);
      formData.append("billID", this.payment_form.billID);
      formData.append("paidVia", this.payment_form.paidVia);
      formData.append("note", this.payment_form.note);
      formData.append("tfimg", this.payment_form.billfile);
      formData.append("bankID", this.payment_form.bankID);
      // formData.append("moreDiscount", this.payment_form.moreDiscount);

      if (this.payment_form.billID == 0) {
        this.createPayment(formData)
          .then((resp) => {
            this.listData(this.searchParams);
            this.listBilling({ tID: this.payment_form.tID });
            swal.fire({
              title: this.$t("edit_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch((err) => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: this.getErrorText(err.response.data),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        this.createBillingPayment(formData)
          .then((resp) => {
            this.listData(this.searchParams);
            this.listBilling({ tID: this.payment_form.tID });
            swal.fire({
              title: this.$t("edit_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch((err) => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: this.getErrorText(err.response.data),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },

    // Display helpers for the payment / billing modals (local maps).
    paymentMethodName(id) {
      const m = this.paymentType.find((p) => p.ID == id);
      return m ? m.Name : "";
    },
    billingStatusName(paid) {
      if (paid == 1) return "ชำระแล้ว";
      if (paid == 2) return "รอชำระ";
      return "";
    },
    billingPillClass(paid) {
      if (paid == 1) return "calm__pill--success";
      if (paid == 2) return "calm__pill--blue";
      return "calm__pill--neutral";
    },

    // --- Inline quick-edit ------------------------------------------------
    beginInlineEditPolicy(row) {
      this.inlineEdit = {
        id: row.ID,
        field: "policy",
        value: row.PolicyNumber || "",
        startDate: "",
        endDate: "",
        saving: false,
      };
    },
    beginInlineEditDates(row) {
      this.inlineEdit = {
        id: row.ID,
        field: "dates",
        value: "",
        startDate: this.parseToYMD(row.StartDate) || this.parseShowToYMD(row.StartDateShow),
        endDate: this.parseToYMD(row.EndDate) || this.parseShowToYMD(row.EndDateShow),
        saving: false,
      };
    },
    cancelInlineEdit() {
      this.inlineEdit = { id: null, field: null, value: "", startDate: "", endDate: "", saving: false };
    },
    async saveInlineEditPolicy(row) {
      const next = (this.inlineEdit.value || "").trim();
      if (!next) {
        swal.fire({ icon: "warning", title: "กรุณาระบุหมายเลขกรมธรรม์", timer: 1500, showConfirmButton: false });
        return;
      }
      if (next === row.PolicyNumber) {
        this.cancelInlineEdit();
        return;
      }
      this.inlineEdit.saving = true;
      try {
        await this.updatePolicyNumberAction({ ID: row.ID, policy_number: next });
        this.$store.commit("TransactionV2/PATCH_ROW", { id: row.ID, fields: { PolicyNumber: next } });
        this.cancelInlineEdit();
        swal.fire({ icon: "success", title: this.$t("edit_success"), timer: 1200, showConfirmButton: false });
      } catch (err) {
        const key = err && err.response && err.response.data && err.response.data.Msg;
        swal.fire({ icon: "error", title: key ? this.$t(key) : this.$t("edit_fail") });
      } finally {
        this.inlineEdit.saving = false;
      }
    },
    async saveInlineEditDates(row) {
      const { startDate, endDate } = this.inlineEdit;
      if (!startDate || !endDate) {
        swal.fire({ icon: "warning", title: "กรุณาเลือกวันที่ให้ครบถ้วน", timer: 1500, showConfirmButton: false });
        return;
      }
      if (new Date(endDate) < new Date(startDate)) {
        swal.fire({ icon: "warning", title: "วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น", timer: 1800, showConfirmButton: false });
        return;
      }
      this.inlineEdit.saving = true;
      try {
        await this.updateDateAction({ ID: row.ID, start_date: startDate, end_date: endDate });
        this.listData(this.searchParams);
        this.cancelInlineEdit();
        swal.fire({ icon: "success", title: this.$t("edit_success"), timer: 1200, showConfirmButton: false });
      } catch (err) {
        const key = err && err.response && err.response.data && err.response.data.Msg;
        swal.fire({ icon: "error", title: key ? this.$t(key) : this.$t("edit_fail") });
      } finally {
        this.inlineEdit.saving = false;
      }
    },
    parseToYMD(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    parseShowToYMD(value) {
      if (!value) return "";
      const parts = String(value).split("/");
      if (parts.length !== 3) return "";
      const [dd, mm, yyyy] = parts;
      if (!dd || !mm || !yyyy) return "";
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    },

    // --- Exports ----------------------------------------------------------
    handleExportCSV() {
      const header = {
        SaleDate: "วันที่ขาย",
        PolicyNumber: "หมายเลขกรมธรรม์",
        LicencePlate: "ทะเบียนรถ",
        OwnerPerson: "ตัวแทน",
        Customer: "ผู้เอาประกัน",
        InsuranceShowName: "ประเภทประกัน",
        StartDate: "วันที่เริ่มต้นคุ้มครอง",
        EndDate: "วันที่สินสุดคุ้มครอง",
        Amount: "เบี้ยสุทธิ",
        AmountAll: "เบี้ยรวม",
        CommissionAmount: "ส่วนลด",
        TaxAmount: "ภาษี",
        Discount: "ส่วนลดพิเศษ",
        PayAmount: "ยอดรวมชำระ",
        Type: "ประเภท",
      };
      const data = this.tableData.map((it) => ({
        SaleDate: it.SaleDate,
        PolicyNumber: it.PolicyNumber,
        LicencePlate: it.LicencePlate,
        OwnerPerson: (it.OwnerPersonFName || "") + " " + (it.OwnerPersonLName || ""),
        Customer: (it.CustomerFname || "") + " " + (it.CustomerLname || ""),
        InsuranceShowName: it.InsuranceShowName,
        StartDate: it.StartDate,
        EndDate: it.EndDate,
        Amount: it.Amount,
        AmountAll: it.AmountAll,
        CommissionAmount: it.CommissionAmount,
        TaxAmount: it.TaxAmount,
        Discount: it.Discount,
        PayAmount: it.PayAmount,
        Type: it.Renew == 2 ? "ต่ออายุ" : "ซื้อประกันใหม่",
      }));
      const csvContent = [header, ...data]
        .map((it) => Object.values(it).toString())
        .join("\n");
      const universalBOM = "\uFEFF";
      const link = document.createElement("a");
      link.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(universalBOM + csvContent));
      link.setAttribute("download", "export.csv");
      document.body.appendChild(link);
      link.click();
    },

    handleExportXLSX() {
      const columns = [
        { key: "SaleDate",          label: "วันที่ขาย" },
        { key: "PolicyNumber",      label: "หมายเลขกรมธรรม์" },
        { key: "LicencePlate",      label: "ทะเบียนรถ" },
        { key: "OwnerPerson",       label: "ตัวแทน" },
        { key: "Customer",          label: "ผู้เอาประกัน" },
        { key: "InsuranceShowName", label: "ประเภทประกัน" },
        { key: "StartDate",         label: "วันที่เริ่มต้นคุ้มครอง" },
        { key: "EndDate",           label: "วันที่สินสุดคุ้มครอง" },
        { key: "Amount",            label: "เบี้ยสุทธิ" },
        { key: "AmountAll",         label: "เบี้ยรวม" },
        { key: "CommissionAmount",  label: "ส่วนลด" },
        { key: "TaxAmount",         label: "ภาษี" },
        { key: "Discount",          label: "ส่วนลดพิเศษ" },
        { key: "PayAmount",         label: "ยอดรวมชำระ" },
        { key: "Type",              label: "ประเภท" },
      ];
      const rows = this.tableData.map((it) => ({
        SaleDate:          it.SaleDate,
        PolicyNumber:      it.PolicyNumber,
        LicencePlate:      it.LicencePlate,
        OwnerPerson:       (it.OwnerPersonFName || "") + " " + (it.OwnerPersonLName || ""),
        Customer:          (it.CustomerFname || "") + " " + (it.CustomerLname || ""),
        InsuranceShowName: it.InsuranceShowName,
        StartDate:         it.StartDate,
        EndDate:           it.EndDate,
        Amount:            it.Amount,
        AmountAll:         it.AmountAll,
        CommissionAmount:  it.CommissionAmount,
        TaxAmount:         it.TaxAmount,
        Discount:          it.Discount,
        PayAmount:         it.PayAmount,
        Type:              it.Renew == 2 ? "ต่ออายุ" : "ซื้อประกันใหม่",
      }));
      const header = columns.map((c) => c.label);
      const body = rows.map((r) => columns.map((c) => r[c.key]));
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      ws["!cols"] = columns.map((c) => ({ wch: Math.max(c.label.length + 2, 14) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `transactions_${today}.xlsx`);
    },
  },
};
</script>


<style scoped>
/*
 * Transaction-list specific tweaks. The base "calm" system (calm.scss) handles
 * cards, buttons, table chrome, pills, inputs, pagination, and the filter
 * grid/sticky layout. This block only carries the bits that are unique to the
 * transactions page: filter-grid responsiveness, action row, the per-row
 * inline edit affordances, the class chip, money cell flagging, and the
 * compact row-action icon buttons.
 */

/* Filter grid: 4 cols by default, with span-2 hooks for the long fields. */
.trans-filter-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 14px;
  margin-top: 4px;
}
.trans-filter-grid .fld--span2 { grid-column: span 2; }

.trans-filter-grid .fld__warn {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.3;
  color: #e6a23c;
}

@media (max-width: 1100px) {
  .trans-filter-grid { grid-template-columns: repeat(2, 1fr); }
  .trans-filter-grid .fld--span2 { grid-column: span 2; }
}
@media (max-width: 600px) {
  .trans-filter-grid { grid-template-columns: 1fr; }
  .trans-filter-grid .fld--span2 { grid-column: span 1; }
}

.trans-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--c-border);
}

/* Table card head: title left, search + per-page right. */
.trans-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 22px;
  border-bottom: 1px solid var(--c-border);
}
.trans-table-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* Stack — owner / customer dual-line cell content. */
.stack { display: flex; flex-direction: column; gap: 1px; }
.stack__primary { color: var(--c-text); font-weight: 500; line-height: 1.35; }
.stack__caption {
  font-size: 11px;
  color: var(--c-text-mute);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
/* Settlement status caption — small pill aligned under the policy number. */
.dms-link {
  color: var(--c-accent, #2563eb);
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}
.dms-link:hover {
  text-decoration-style: solid;
}
.settle-pill {
  align-self: flex-start;
  margin-top: 2px;
  font-size: 10.5px;
  padding: 2px 8px;
}

/* clickable commission-paid toggle pill */
.commission-toggle {
  border: none;
  cursor: pointer;
  font: inherit;
  transition: filter 0.15s ease;
}
.commission-toggle:hover:not(:disabled) {
  filter: brightness(0.95);
}
.commission-toggle:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Class chip — neutral category tag. */
.class-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  background: var(--c-blue-soft);
  color: var(--c-blue-deep);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

/* Money formatting. */
.money { color: var(--c-text); font-weight: 500; }
.money--flag { color: var(--c-warning); }
.money--flag::after {
  content: "\2009*";
  color: var(--c-warning);
  font-weight: 600;
  margin-left: 1px;
}
.money-soft { color: var(--c-text-soft); }

/* Date range presentation. */
.date-range { display: inline-flex; align-items: center; gap: 6px; }
.date-range .dash { color: var(--c-text-mute); font-weight: 400; }

/* Inline-edit cell — pen icon, then expanding edit row. */
.inline-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.inline-cell--edit { gap: 4px; }
.inline-input {
  font-family: var(--c-font-mono);
  font-size: 13px;
  color: var(--c-text);
  background: var(--c-surface);
  border: 1px solid var(--c-border-strong);
  border-radius: 6px;
  padding: 6px 8px;
  height: 30px;
  width: 100%;
  min-width: 100px;
  max-width: 220px;
  transition: border-color var(--c-fast) var(--c-ease), box-shadow var(--c-fast) var(--c-ease);
}
.inline-input:focus {
  outline: none;
  border-color: var(--c-blue);
  box-shadow: 0 0 0 3px rgba(59, 110, 168, 0.12);
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  background: transparent;
  border: 0;
  color: var(--c-text-mute);
  border-radius: 6px;
  cursor: pointer;
  transition: background var(--c-fast) var(--c-ease), color var(--c-fast) var(--c-ease);
}
.icon-btn:hover:not(:disabled) {
  background: var(--c-blue-soft);
  color: var(--c-blue-deep);
}
.icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.icon-btn--confirm { color: var(--c-success); }
.icon-btn--confirm:hover:not(:disabled) {
  background: var(--c-success-soft);
  color: var(--c-success);
}

/* Row action cluster — small square icon buttons. */
.row-actions {
  display: inline-flex;
  gap: 4px;
  justify-content: flex-end;
}
.rowbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  color: var(--c-text-soft);
  border-radius: 6px;
  cursor: pointer;
  transition: all var(--c-fast) var(--c-ease);
  font-size: 12.5px;
}
.rowbtn:hover {
  background: var(--c-blue-soft);
  border-color: var(--c-blue);
  color: var(--c-blue-deep);
}

/* flatpickr inside our calm inputs — match height + border style. */
.calm >>> .flatpickr-input.ed,
.calm /deep/ .flatpickr-input.ed {
  background: var(--c-surface);
}

/* Report / Cancel modal bodies. */
.trans-report .fld,
.trans-cancel .fld { margin-bottom: 14px; }
.trans-report .fld:last-child,
.trans-cancel .fld:last-child { margin-bottom: 0; }
.trans-report .fld > label,
.trans-cancel .fld > label {
  display: block;
  font-size: 12px;
  color: var(--c-text-soft);
  margin-bottom: 6px;
}
.trans-cancel__id {
  margin-bottom: 14px;
  font-size: 13px;
  color: var(--c-text-soft);
}
.trans-cancel__id strong { color: var(--c-text); }
.trans-cancel textarea.ed {
  width: 100%;
  resize: vertical;
  min-height: 72px;
}

/* Payment / Billing modal bodies. */
.pay-modal { margin: -4px 0; }
.pay-form .fld { margin-bottom: 14px; }
.pay-form .fld:last-child { margin-bottom: 0; }
.pay-form .fld > label {
  display: block;
  font-size: 12px;
  color: var(--c-text-soft);
  margin-bottom: 6px;
}
.pay-form textarea.ed {
  width: 100%;
  resize: vertical;
  min-height: 56px;
}
.pay-form .el-select { width: 100%; }
</style>
